/**
 * Public Page Metadata
 * - title: Browser tab title (appended with " | TheScope180")
 * - description: SEO-optimized meta description (150-160 chars, keyword-rich, with CTAs)
 * - ogImage: Open Graph image URL (optional, key pages)
 */

export const publicPageMeta = {
  /* ===================== HOME & MAIN ===================== */
  "/": {
    title: "Home",
    description: "AI-powered PMP exam prep, project management courses, and career coaching. Get certified faster with TheScope180's adaptive learning platform. Start free today.",
    ogImage: "https://www.thescope180.com/og-home.png",
  },

  "/aboutme": {
    title: "About TheScope180",
    description: "Meet the team behind TheScope180 — a career-focused training academy built to give PMs clarity, confidence, and real-world skills that get results.",
  },

  "/contact": {
    title: "Contact Us",
    description: "Have questions about PMP prep, courses, or consulting? Contact TheScope180 and our team will get back to you within 24 hours. We're here to help.",
  },


  "/portfolio": {
    title: "Work Samples",
    description: "Explore TheScope180's portfolio of consulting projects, training programs, and PM tools. See real results from real engagements across industries.",
  },

  /* ===================== COURSES & PRODUCTS ===================== */
  "/academy": {
    title: "Academy",
    description: "Browse PMP, CAPM, and AI certification courses with adaptive learning, video lessons, and practice exams. Enroll today and advance your PM career.",
    ogImage: "https://www.thescope180.com/og-courses.png",
  },

  "/products": {
    title: "Products",
    description: "PMP practice exams, the free PM Simulator and Daily 5, learning tools, mini courses and the Deliverables Suite. Some free, the rest included with Premium or sold on their own.",
    ogImage: "https://www.thescope180.com/og-products.png",
  },

  "/practice-exams": {
    title: "Practice Exams",
    description: "PMP practice exams from a 300-question bank written for PMBOK 8 and the July 2026 ECO, with an explanation for every answer and domain analytics. Included with Premium.",
  },


  /* ===================== BLOG ===================== */
  "/blog": {
    title: "Blog",
    description: "Expert insights on PMP exam tips, project management trends, Agile methodology, and career growth. Read actionable articles from TheScope180's PM experts.",
    ogImage: "https://www.thescope180.com/og-blog.png",
  },

  /* ===================== DELIVERABLES SUITE ===================== */
  "/deliverables": {
    title: "Deliverables Suite — 40+ AI-Powered PM Document Generators",
    description: "Generate business cases, project charters, RACI matrices, risk registers, budgets, and 40+ executive-ready PM documents in minutes. Built by PMs, powered by TheScopeAI.",
    ogImage: "https://www.thescope180.com/og-deliverables.png",
  },

  /* ===================== TOOLS & SIMULATORS ===================== */
  "/pm-certification-pathfinder": {
    title: "PM Certification Pathfinder",
    description: "Not sure which PM certification to pursue? Take TheScope180's free Pathfinder quiz and get a personalized recommendation — PMP, CAPM, PMI-ACP, or CPMAI.",
    ogImage: "https://www.thescope180.com/og-pathfinder.png",
  },

  "/pathfinder/summary": {
    title: "Pathfinder Results",
    description: "Your personalized PM certification recommendation is ready. See which certification fits your experience level, career goals, and learning style.",
  },

  "/learn/ai-risk-snapshot": {
    title: "AI Risk Snapshot",
    description: "Identify project threats in 10 minutes with AI-assisted risk brainstorming. Free interactive tool for PMP aspirants, Agile PMs, and corporate teams.",
  },

  "/recommended-training": {
    title: "Recommended Training",
    description: "Get AI-personalized training recommendations based on your skill level, certification goals, and learning pace. Your custom PM development roadmap awaits.",
  },

  /* ===================== AUTH ===================== */
  "/login": {
    title: "Login",
    description: "Sign in to your TheScope180 account to access courses, track progress, and continue your PM certification journey. New here? Create a free account.",
  },

  "/register": {
    title: "Register",
    description: "Create your free TheScope180 account and unlock AI-powered PMP prep, practice exams, and personalized learning paths. Join thousands of PMs today.",
  },

  "/verify-email": {
    title: "Verify Email",
    description: "Almost there! Verify your email address to activate your TheScope180 account and start learning. Check your inbox for the confirmation link.",
  },

  "/email-confirmed": {
    title: "Email Confirmed",
    description: "Your email has been confirmed. Welcome to TheScope180! Head to your dashboard to explore courses and start your certification prep.",
  },

  "/welcome": {
    title: "Welcome",
    description: "Welcome to TheScope180! Your account is ready. Explore PMP courses, practice exams, and AI-powered study tools designed for your success.",
  },

  /* ===================== CHECKOUT ===================== */
  "/checkout": {
    title: "Checkout",
    description: "Complete your purchase securely with TheScope180. All transactions are encrypted and protected. Instant access to your courses and resources after payment.",
  },

  "/checkout/success": {
    title: "Purchase Complete",
    description: "Thank you for your purchase! Your courses and resources are now unlocked. Head to your dashboard to start learning right away.",
  },

  /* ===================== WAITLIST ===================== */
  "/waitlist": {
    title: "Join Waitlist",
    description: "Be the first to know when new courses launch. Join TheScope180's waitlist for early access, exclusive discounts, and priority enrollment. Don't miss out.",
  },

  "/waitlist/thanks": {
    title: "Thank You",
    description: "You're on the waitlist! We'll notify you as soon as new courses are available. In the meantime, explore our blog and free PM resources.",
  },

  "/waitlist/login": {
    title: "Waitlist Login",
    description: "Check your waitlist status and manage your preferences. Log in to see your position and get updates on upcoming TheScope180 courses.",
  },

  /* ===================== LEGAL ===================== */
  "/terms": {
    title: "Terms of Service",
    description: "Read TheScope180's terms of service covering account usage, course access, refund policies, and intellectual property. Updated for 2025.",
  },

  "/privacy": {
    title: "Privacy Policy",
    description: "Learn how TheScope180 collects, uses, and protects your personal data. We're committed to your privacy and GDPR compliance. Read our full policy.",
  },

  /* ===================== CAREERS ===================== */
  "/careers": {
    title: "Careers",
    description: "Explore career opportunities at TheScope180. We're not currently hiring, but check back soon for open positions on our team.",
  },

  /* ===================== OTHER ===================== */
  "/unauthorized": {
    title: "Unauthorized",
    description: "You don't have access to this page. Please log in or contact support if you believe this is an error.",
  },

  "/certificate-preview": {
    title: "Certificate Preview",
    description: "Preview your TheScope180 course completion certificate. Share your achievement on LinkedIn and add it to your professional portfolio.",
  },
};
